// Helpers for the grey pre-fill (see the PredictionState comment in state/AnswersContext.tsx).

import type { AnswerState, Question, Tab } from "../types";

/** Every kind of empty answer: unset, null, blank text, an unselected option. */
export const isBlankAnswer = (v: unknown) => v === undefined || v === null || v === "" || v === false;

/** Same answer for prediction purposes: every kind of empty is equal, otherwise by text, so a
 *  number and its string ("4" / 4) match the way the form shows them. */
export function sameAnswer(a: unknown, b: unknown): boolean {
  if (isBlankAnswer(a) && isBlankAnswer(b)) return true;
  if (isBlankAnswer(a) || isBlankAnswer(b)) return false;
  return String(a) === String(b);
}

/** The tabs a user can see, in order (the internal DEBUG tab excluded). */
export function visibleTabsInOrder(tabs: Tab[]): Tab[] {
  return tabs
    .filter((t) => t.custrecord_cpqc_tab_code !== "DEBUG")
    .sort((a, b) => Number(a.custrecord_cpqc_tab_sequence || 0) - Number(b.custrecord_cpqc_tab_sequence || 0));
}

const questionOf = (key: string) => {
  const i = key.indexOf("/");
  return i < 0 ? key : key.slice(0, i);
};

/** Answer keys whose question sits on the given tab. */
export function keysOnTab(questions: Question[], tabId: string): (key: string) => boolean {
  const on = new Set(
    questions.filter((q) => q.custrecord_cpqc_question_tab === tabId).map((q) => q.custrecord_cpqc_question_code)
  );
  return (key) => on.has(questionOf(key));
}

/** Answer keys a user sees and could change: questions on a visible tab (table cells
 *  included). Computed text -- description, id key, memory shadows -- is on no visible tab. */
/** Whether a prediction is WRITTEN INTO THE FORM when it is made. Kevin, 2026-10-19: "default
 *  the prediction to off. however it should still predict in the background so we can compare
 *  what was on the cpq vs what was predicted." Off: the prediction is computed and kept, the
 *  header switch fills it in on demand, and Save & Submit records the prediction against what
 *  the rep configured either way (cpq2_prediction_outcomes.changed_detail). A Recent pick and
 *  "Configure the opposite side" are the rep's own choice, so those always show. */
export const PREDICTION_DEFAULT_ON = false;

export function scoredKeys(questions: Question[], tabs: Tab[]): (key: string) => boolean {
  const visible = new Set(visibleTabsInOrder(tabs).map((t) => t.id));
  const codes = new Set(
    questions
      .filter((q) => visible.has(q.custrecord_cpqc_question_tab) && q.custrecord_cpqc_question_code !== "DESCRIPTION")
      .map((q) => q.custrecord_cpqc_question_code)
  );
  return (key) => codes.has(questionOf(key));
}

/** The answers on one page, for the page-2 refresh. */
export function pageAnswers(answers: AnswerState, onPage: (key: string) => boolean): AnswerState {
  const out: AnswerState = {};
  for (const [k, v] of Object.entries(answers)) if (onPage(k)) out[k] = v;
  return out;
}

/**
 * PAGE 1 FIRST (Kevin, 2026-10-17): "Do not select the burner or press generate housing options.
 * Have the user do that. This is so when they are ready they will select and you can do the
 * prediction for fire tube because based on what they have in that first page you'll be able to
 * give a better prediction for the next page."
 *
 * So a launch prediction fills only the first page's own inputs. Left to the user: the housing
 * choice -- the Housing Options table (its generated rows, its selection, or the hand-entered
 * row of a replacement/BYOB), the Generate Housing Options button, and the Housing / model text
 * they fill -- and the values that button computes, which would otherwise show another job's
 * numbers until it is pressed. The pages after the first wait for the housing
 * (components/PredictionRefresh). The MB mirror and a Recent pick still load whole: the user
 * chose those configurations themselves.
 */
const HOUSING_CHOICE = new Set(["BURNER_TYPE", "CALCULATE2", "HOUSING", "HOUSING_VALIDATION", "SELECTION", "MODEL"]);
const COMPUTED_BY_GENERATE = new Set(["ACTUAL_HEAT_FLUX", "AIR_PRESSURE", "FIRETUBE_SURFACE_AREA", "REQUIRED_CELL_AREA", "FUEL_DENSITY"]);
export function launchKeys(questions: Question[], tabs: Tab[]): (key: string) => boolean {
  const first = visibleTabsInOrder(tabs)[0];
  if (!first) return () => false;
  const onFirst = keysOnTab(questions, first.id);
  return (key) => onFirst(key) && !HOUSING_CHOICE.has(questionOf(key)) && !COMPUTED_BY_GENERATE.has(questionOf(key));
}

/** The housing is chosen: HOUSING holds the selected row's housing (op 357 sets it from the
 *  selection, or from the hand-entered replacement row). What the page-2 prediction waits for.
 *  Not the model text -- op 357 assembles that before anything is selected. */
export const housingChosen = (answers: AnswerState) => !isBlankAnswer(String(answers["HOUSING/HOUSING"] ?? "").trim());

/** Hover text (and accessible description) of an answer the MB mirror swapped from the other
 *  burner -- the slate mark on its own says nothing to a screen reader. */
export const MIRRORED_TITLE = "Swapped left/right from the opposite side. Check it.";

/**
 * What a predicted text field shows while it is being typed into (components/PredictedText,
 * ComboBox). Kevin, 2026-10-17: clicking into a predicted field must not mean backspacing it
 * away, and clicking out without typing brings the prediction back. So while focused and not
 * yet typed in, the box is empty and the prediction is its placeholder; otherwise it shows the
 * value. The stored answer is the prediction throughout, until something is typed.
 */
export function ghostView(ghosting: boolean, value: string, placeholder?: string): { value: string; placeholder?: string } {
  return ghosting ? { value: "", placeholder: value } : { value, placeholder };
}
